import { useRegisterAccountMutation } from 'src/api/endpoints/students/personal/api';
import { PhoneField } from 'src/components/design-system/form/fields/PhoneField';
import { AuthorizedUserProps } from 'src/routes/components/PrivateRoute';
import { AvatarUploadWidget } from 'src/components/widgets/AvatarUpload';
import { FormButtons } from 'src/components/design-system/form/buttons';
import React, { FC, PropsWithChildren, ReactElement } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { FormRow, TextField } from 'src/forms/DebitStudentCoins';
import { Form } from 'src/components/design-system/form';
import { notifyAboutException } from 'src/utils/notify';
import { FormikErrors, useFormik } from 'formik';
import { ResponseError } from 'src/api';
import './styles.scss';

export type ProfileFillingForm = {
    firstname: string;
    surname: string;
    patronymic?: string;

    email: string;
    phone?: {
        raw: string;
        formatted: string;
    };
    formattedPhone: string;

    birthday: Date;
};

export const IntroducingPage: FC<AuthorizedUserProps> = () => {
    const navigate: NavigateFunction = useNavigate();

    const [registerAccount] = useRegisterAccountMutation();

    const formik = useFormik<Partial<ProfileFillingForm>>({
        initialValues: {},
        validate: (values: Partial<ProfileFillingForm>) => {
            const { email, phone, firstname, surname } = values;

            const errors: FormikErrors<ProfileFillingForm> = {};

            if (!firstname || !firstname.trim().length) {
                errors.firstname = 'Поле обязательно к заполнению';
            }

            if (!surname || !surname.trim().length) {
                errors.surname = 'Поле обязательно к заполнению';
            }

            if (phone?.raw && phone.raw.length !== 11) {
                errors.phone = 'Значение не является номером телефона';
            }

            if (!email) {
                errors.email = 'Поле обязательно к заполнению';
            } else if (!email.match(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/)) {
                errors.email = 'Значение не является адресом электронной почты';
            }

            return errors;
        },
        onSubmit: async (
            values: Partial<ProfileFillingForm>
        ): Promise<void> => {
            const v = values as Required<ProfileFillingForm>;

            await registerAccount({
                body: {
                    firstname: v.firstname,
                    surname: v.surname,
                    patronymic:
                        v.patronymic && v.patronymic.trim().length !== 0
                            ? v.patronymic.trim()
                            : null,
                    email: v.email,
                    phone: v.phone.formatted,
                },
            })
                .unwrap()
                .then(() => {
                    navigate('/');
                })
                .catch((error: ResponseError) => {
                    notifyAboutException({
                        content: error.message,
                    });
                });
        },
    });

    return (
        <main>
            <section className="form-filling">
                <Form prohibitAutoComplete={true} formik={formik}>
                    <div className="content-block contact">
                        <div className={'block-head'}>
                            <div className="block-header">
                                Основная информация
                            </div>
                            <div className="block-description">
                                Нужно, чтобы тебя узнавали наши сотрудники
                            </div>
                        </div>
                        <FormFieldGroup>
                            <AvatarUploadWidget />
                            <FormRow>
                                <TextField
                                    label={'Фамилия*'}
                                    name={'surname'}
                                    formik={formik}
                                />

                                <TextField
                                    label={'Имя*'}
                                    name={'firstname'}
                                    formik={formik}
                                />

                                <TextField
                                    label={'Отчество'}
                                    name={'patronymic'}
                                    formik={formik}
                                />
                            </FormRow>
                        </FormFieldGroup>
                    </div>

                    <div className="content-block contact">
                        <div className={'block-head'}>
                            <div className="block-header">
                                Контактная информация
                            </div>
                            <div className="block-description">
                                Понадобится для уведомлений и поддержки связи по
                                вопросам обучения
                            </div>
                        </div>

                        <FormFieldGroup gap={8}>
                            <TextField
                                autoComplete={'email'}
                                label={'Адрес электронной почты*'}
                                name={'email'}
                                formik={formik}
                            />

                            <PhoneField
                                label={'Номер телефона*'}
                                name={'phone'}
                                formik={formik}
                            />
                        </FormFieldGroup>
                    </div>
                    <FormButtons formik={formik} showCancel={false} />
                </Form>
            </section>
        </main>
    );
};

export type FormFieldGroupProps = PropsWithChildren<{
    // Default 8px
    gap?: string | number;
}>;

export const FormFieldGroup: FC<FormFieldGroupProps> = (
    props: FormFieldGroupProps
): ReactElement => {
    const { children, gap = '8px' } = props;

    return (
        <div className={'form-field-group'} style={{ gap }}>
            {children}
        </div>
    );
};
